import React, { useState, useEffect } from 'react';
import { MapPin, Users, Clock, Share2, Eye, Navigation, Loader2, Globe, X, Plus } from 'lucide-react';

// Replace this with your actual Cloudflare Worker URL after deployment
const API_BASE_URL = 'https://travel-location-api.broken-shape-0a92.workers.dev';

const TravelLocationApp = () => {
  const [view, setView] = useState('home');
  const [travelerName, setTravelerName] = useState('');
  const [shareCode, setShareCode] = useState('');
  const [location, setLocation] = useState(null);
  const [isSharing, setIsSharing] = useState(false);
  const [watchId, setWatchId] = useState(null);
  const [viewerCode, setViewerCode] = useState('');
  const [savedCodes, setSavedCodes] = useState([]);
  const [activeTravelers, setActiveTravelers] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [allTravelers, setAllTravelers] = useState([]);
  const [showBrowserWarning, setShowBrowserWarning] = useState(false);
  const [showPWAPrompt, setShowPWAPrompt] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [notificationSchedule, setNotificationSchedule] = useState('every3hours');
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);
  const [viewerId, setViewerId] = useState('');
  const [tempNotificationSchedule, setTempNotificationSchedule] = useState('every3hours');
  const [scheduleChanged, setScheduleChanged] = useState(false);
  const [scheduleSaved, setScheduleSaved] = useState(false);
  const [approximateLocation, setApproximateLocation] = useState(false);

  // Check if user is on iOS Chrome
  const isIOSChrome = () => {
    const ua = navigator.userAgent;
    return /CriOS/i.test(ua) && /iPhone|iPad|iPod/i.test(ua);
  };

  // Check if running as PWA
  const isPWA = () => {
    return window.matchMedia('(display-mode: standalone)').matches || 
           window.navigator.standalone === true;
  };

  // Check if on iOS
  const isIOS = () => {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
  };

  useEffect(() => {
    const saved = localStorage.getItem('saved_travel_codes');
    if (saved) {
      const codes = JSON.parse(saved);
      setSavedCodes(codes);
      
      if (codes.length > 0) {
        loadSavedTravelers(codes);
      }
    }

    // Check if URL has a code parameter
    const urlParams = new URLSearchParams(window.location.search);
    const codeFromUrl = urlParams.get('code');
    
    if (codeFromUrl) {
      // Auto-load the traveler from the shared link
      setViewerCode(codeFromUrl.toUpperCase());
      // Trigger viewing after a brief moment
      setTimeout(() => {
        viewTravelerFromUrl(codeFromUrl.toUpperCase());
      }, 500);
    }

    // Check notification permission status
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }

    // Register service worker for push notifications
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      navigator.serviceWorker.register('/service-worker.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration);
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    }

    // Generate or retrieve unique viewer ID
    let storedViewerId = localStorage.getItem('viewer_id');
    if (!storedViewerId) {
      storedViewerId = 'viewer_' + Math.random().toString(36).substring(2, 15);
      localStorage.setItem('viewer_id', storedViewerId);
    }
    setViewerId(storedViewerId);
  }, []);

  const viewTravelerFromUrl = async (code) => {
    setLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/viewer/${code}`, {
        headers: {
          'X-Viewer-ID': viewerId
        }
      });
      const data = await response.json();
      
      if (data.success && data.traveler) {
        addCodeToSaved(code);
        const codes = savedCodes.includes(code) ? savedCodes : [...savedCodes, code];
        await loadSavedTravelers(codes);
      } else {
        setError('Invalid share link or traveler not found');
        setView('home');
      }
    } catch (err) {
      setError('Failed to load traveler. Check the link and try again.');
      setView('home');
    } finally {
      setLoading(false);
    }
  };

  const loadSavedTravelers = async (codes) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/viewer/multiple`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ codes })
      });
      
      const data = await response.json();
      if (data.success && data.travelers.length > 0) {
        setActiveTravelers(data.travelers);
        setView('saved-travelers');
      }
    } catch (err) {
      console.error('Error loading travelers:', err);
    }
  };

  const generateShareCode = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  const getCurrentPosition = () => {
    return new Promise((resolve, reject) => {
      console.log('[DEBUG] Checking geolocation support...');
      
      if (!navigator.geolocation) {
        console.error('[ERROR] Geolocation not supported');
        reject(new Error('Geolocation not supported by your browser'));
        return;
      }
      
      console.log('[DEBUG] Geolocation supported, requesting position...');
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          console.log('[DEBUG] Position success!', position);
          resolve(position);
        },
        (error) => {
          console.error('[ERROR] Geolocation error:', error);
          console.error('[ERROR] Error code:', error.code);
          console.error('[ERROR] Error message:', error.message);
          reject(new Error(`Location error (code ${error.code}): ${error.message}`));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  };

  const startSharing = async () => {
    if (!travelerName.trim()) {
      setError('Please enter your name');
      return;
    }

    // Check if iOS Chrome
    if (isIOSChrome()) {
      setShowBrowserWarning(true);
      return;
    }

    // Check if not PWA on iOS (optional but recommended)
    if (isIOS() && !isPWA()) {
      setShowPWAPrompt(true);
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('[DEBUG] Starting location share...');
      console.log('[DEBUG] Getting current position...');
      
      const position = await getCurrentPosition();
      console.log('[DEBUG] Position received:', position.coords);
      
      const code = generateShareCode();
      console.log('[DEBUG] Generated code:', code);
      
      const locationData = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: new Date().toISOString()
      };

      console.log('[DEBUG] Sending to API:', API_BASE_URL);
      console.log('[DEBUG] Location data:', locationData);

      const response = await fetch(`${API_BASE_URL}/api/traveler/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: travelerName,
          code,
          location: locationData
        })
      });

      console.log('[DEBUG] API Response status:', response.status);
      const result = await response.json();
      console.log('[DEBUG] API Result:', result);
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to start sharing');
      }

      setLocation(locationData);
      setShareCode(code);

      const id = navigator.geolocation.watchPosition(
        async (pos) => {
          let newLocation = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            timestamp: new Date().toISOString()
          };

          // Use approximate location if enabled
          if (approximateLocation) {
            const approx = getApproximateLocation(newLocation.latitude, newLocation.longitude);
            newLocation = {
              ...newLocation,
              latitude: approx.latitude,
              longitude: approx.longitude,
              accuracy: 11000,
              isApproximate: true
            };
          }

          setLocation(newLocation);
          
          try {
            await fetch(`${API_BASE_URL}/api/traveler/update`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ code, location: newLocation })
            });
          } catch (err) {
            console.error('Error updating location:', err);
          }
        },
        (err) => console.error('Location error:', err),
        {
          enableHighAccuracy: true,
          maximumAge: 30000,
          timeout: 27000
        }
      );

      setWatchId(id);
      setIsSharing(true);
      setView('traveler');
      
      // Start polling for viewer count
      pollViewerCount(code);
      
      // Setup push notifications
      setupPushNotifications(code);
    } catch (err) {
      console.error('[ERROR] Full error:', err);
      console.error('[ERROR] Error name:', err.name);
      console.error('[ERROR] Error message:', err.message);
      console.error('[ERROR] Error stack:', err.stack);
      
      const errorMsg = `${err.message || 'Failed to get location'} (Check console for details)`;
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const stopSharing = async () => {
    if (watchId) {
      navigator.geolocation.clearWatch(watchId);
      setWatchId(null);
    }
    
    // Stop polling viewer count
    if (window.viewerCountInterval) {
      clearInterval(window.viewerCountInterval);
      window.viewerCountInterval = null;
    }
    
    try {
      await fetch(`${API_BASE_URL}/api/traveler/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: shareCode })
      });
    } catch (err) {
      console.error('Error stopping share:', err);
    }

    setIsSharing(false);
    setView('home');
    setShareCode('');
    setLocation(null);
    setTravelerName('');
  };

  const addCodeToSaved = (code) => {
    const upperCode = code.toUpperCase();
    if (!savedCodes.includes(upperCode)) {
      const newCodes = [...savedCodes, upperCode];
      setSavedCodes(newCodes);
      localStorage.setItem('saved_travel_codes', JSON.stringify(newCodes));
    }
  };

  const removeCodeFromSaved = (code) => {
    const newCodes = savedCodes.filter(c => c !== code);
    setSavedCodes(newCodes);
    localStorage.setItem('saved_travel_codes', JSON.stringify(newCodes));
    
    if (newCodes.length === 0) {
      setActiveTravelers([]);
      setView('home');
    } else {
      loadSavedTravelers(newCodes);
    }
  };

  const viewTravelers = async () => {
    if (!viewerCode.trim()) {
      setError('Please enter a share code');
      return;
    }

    setError('');
    setLoading(true);
    
    const upperCode = viewerCode.toUpperCase();

    try {
      const response = await fetch(`${API_BASE_URL}/api/viewer/${upperCode}`, {
        headers: {
          'X-Viewer-ID': viewerId
        }
      });
      const data = await response.json();
      
      if (data.success && data.traveler) {
        addCodeToSaved(upperCode);
        const codes = savedCodes.includes(upperCode) ? savedCodes : [...savedCodes, upperCode];
        await loadSavedTravelers(codes);
        setViewerCode('');
      } else {
        setError('Invalid share code or traveler not found');
      }
    } catch (err) {
      setError('Failed to fetch traveler. Check the code and try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if ((view === 'saved-travelers' || view === 'viewer') && activeTravelers.length > 0) {
      const interval = setInterval(async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/viewer/multiple`, {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json',
              'X-Viewer-ID': viewerId
            },
            body: JSON.stringify({ codes: savedCodes })
          });
          
          const data = await response.json();
          if (data.success) {
            setActiveTravelers(data.travelers);
          }
        } catch (err) {
          console.error('Error polling updates:', err);
        }
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [view, activeTravelers, savedCodes]);

  const formatCoord = (num) => num?.toFixed(6) || 'N/A';

  const getLocalTime = (lat, lng) => {
    const offset = Math.round(lng / 15);
    const d = new Date();
    d.setHours(d.getUTCHours() + offset);
    return d.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  // Get approximate location (nearest city center)
  const getApproximateLocation = (lat, lon) => {
    // Round to ~11km precision (0.1 degrees ~ 11km)
    return {
      latitude: Math.round(lat * 10) / 10,
      longitude: Math.round(lon * 10) / 10
    };
  };

  // Format time since last update
  const getLastUpdateText = (timestamp) => {
    if (!timestamp) return 'Never';
    const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (seconds < 10) return 'Just now';
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  // Admin functions
  const loginAdmin = async () => {
    if (!adminPassword.trim()) {
      setError('Please enter admin password');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/travelers`, {
        headers: {
          'X-Admin-Password': adminPassword
        }
      });

      if (response.status === 401) {
        setError('Invalid password');
        setLoading(false);
        return;
      }

      const data = await response.json();
      
      if (data.success) {
        setIsAdminAuthenticated(true);
        setAllTravelers(data.travelers);
        setView('admin');
      } else {
        setError('Failed to fetch travelers');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const refreshAdminData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/travelers`, {
        headers: {
          'X-Admin-Password': adminPassword
        }
      });

      const data = await response.json();
      if (data.success) {
        setAllTravelers(data.travelers);
      }
    } catch (err) {
      console.error('Failed to refresh admin data:', err);
    }
  };

  const deleteAdminTraveler = async (code) => {
    if (!window.confirm(`Delete traveler ${code}?`)) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/traveler/${code}`, {
        method: 'DELETE',
        headers: {
          'X-Admin-Password': adminPassword
        }
      });

      const data = await response.json();
      if (data.success) {
        await refreshAdminData();
      }
    } catch (err) {
      console.error('Failed to delete traveler:', err);
    }
  };

  const logoutAdmin = () => {
    setIsAdminAuthenticated(false);
    setAdminPassword('');
    setAllTravelers([]);
    setView('home');
  };

  // Poll for viewer count while sharing
  const pollViewerCount = async (code) => {
    const interval = setInterval(async () => {
      try {
        // Don't send viewer ID when traveler is checking their own count
        const response = await fetch(`${API_BASE_URL}/api/viewer/${code}`);
        const data = await response.json();
        
        if (data.success && data.traveler) {
          setViewerCount(data.traveler.viewCount || 0);
        }
      } catch (err) {
        console.error('Failed to fetch viewer count:', err);
      }
    }, 10000); // Check every 10 seconds

    // Store interval ID for cleanup
    window.viewerCountInterval = interval;
  };

  // Request notification permission and subscribe
  const setupPushNotifications = async (code) => {
    if (!('Notification' in window)) {
      console.log('Notifications not supported');
      return false;
    }

    if (!('serviceWorker' in navigator)) {
      console.log('Service Workers not supported');
      return false;
    }

    try {
      // Request permission
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);

      if (permission !== 'granted') {
        console.log('Notification permission denied');
        return false;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push notifications
      // Note: You'll need to generate VAPID keys for production
      // For now, this is a placeholder
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(
          'BEl62iUYgUivxIkv69yViEuiBIa-Ib37J8xQmrMQjGC7qJ_W7G9-4pFKvN_qpqM3E_qGm0qr2s6J-L2qIJ_qmM0' // Placeholder - generate your own!
        )
      });

      // Send subscription to backend
      await fetch(`${API_BASE_URL}/api/traveler/subscribe-push`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          subscription: subscription.toJSON()
        })
      });

      console.log('Push notification subscription successful');
      return true;
    } catch (error) {
      console.error('Error setting up push notifications:', error);
      return false;
    }
  };

  // Helper function to convert VAPID key
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // Update notification schedule
  const updateNotificationSchedule = async (schedule) => {
    if (!shareCode) return;

    try {
      await fetch(`${API_BASE_URL}/api/traveler/update-schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: shareCode,
          schedule
        })
      });

      setNotificationSchedule(schedule);
      setTempNotificationSchedule(schedule);
      setScheduleChanged(false);
      setScheduleSaved(true);

      // Hide saved indicator after 3 seconds
      setTimeout(() => {
        setScheduleSaved(false);
      }, 3000);
    } catch (error) {
      console.error('Failed to update schedule:', error);
    }
  };

  // Handle schedule toggle change
  const handleScheduleChange = (newSchedule) => {
    setTempNotificationSchedule(newSchedule);
    setScheduleChanged(newSchedule !== notificationSchedule);
    setScheduleSaved(false);
  };

  // Save notification schedule
  const saveNotificationSchedule = () => {
    updateNotificationSchedule(tempNotificationSchedule);
  };

  // Test notification
  const testNotification = async () => {
    if (notificationPermission !== 'granted') {
      alert('Please enable notifications first!');
      return;
    }

    // Show local notification as test
    if ('Notification' in window) {
      new Notification('📍 Test Notification', {
        body: 'This is what your reminder will look like! Close the app and wait 10 seconds to test real push notifications.',
        icon: '/logo192.png',
        badge: '/logo192.png'
      });
    }

    alert('Test notification sent! To test push notifications:\n\n1. Close this window\n2. Wait 10 seconds\n3. You should receive a notification\n4. Tap it to reopen the app');
  };

  if (view === 'home') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="max-w-md mx-auto pt-12">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
              <MapPin className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Travel Share Lite</h1>
            <p className="text-gray-600">Share your location in real-time</p>
          </div>

          {/* iOS Chrome Warning Modal */}
          {showBrowserWarning && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-3xl">⚠️</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Use Safari Instead</h3>
                  <p className="text-gray-600 text-sm">
                    Location sharing works best in Safari on iPhone. Chrome on iOS has limited location permissions.
                  </p>
                </div>
                
                <div className="space-y-3">
                  <button
                    onClick={() => {
                      // Copy current URL to clipboard
                      navigator.clipboard.writeText(window.location.href);
                      // Try to open in Safari (iOS will prompt)
                      window.location.href = window.location.href.replace(/^https?:\/\//, 'x-safari-https://');
                    }}
                    className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition"
                  >
                    🧭 Open in Safari
                  </button>
                  
                  <button
                    onClick={() => setShowBrowserWarning(false)}
                    className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-200 transition"
                  >
                    Continue Anyway
                  </button>
                </div>
                
                <div className="mt-4 p-3 bg-blue-50 rounded-lg text-xs text-blue-800">
                  <p className="font-semibold mb-1">📋 Manual Steps:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Copy this URL (already copied!)</li>
                    <li>Open Safari</li>
                    <li>Paste and visit the URL</li>
                  </ol>
                </div>
              </div>
            </div>
          )}

          {/* PWA Prompt Modal */}
          {showPWAPrompt && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm">
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <span className="text-3xl">📱</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Install App for Best Experience</h3>
                  <p className="text-gray-600 text-sm">
                    For reliable location sharing, we recommend installing this as an app on your home screen.
                  </p>
                </div>
                
                <div className="mb-4 p-4 bg-purple-50 rounded-lg text-sm">
                  <p className="font-semibold text-purple-900 mb-2">📱 How to Install:</p>
                  <ol className="list-decimal list-inside space-y-1 text-purple-800">
                    <li>Tap the <strong>Share</strong> button below ↓</li>
                    <li>Scroll and tap <strong>"Add to Home Screen"</strong></li>
                    <li>Tap <strong>"Add"</strong></li>
                    <li>Open from your home screen!</li>
                  </ol>
                </div>
                
                <div className="space-y-3">
                  <button
                    onClick={() => setShowPWAPrompt(false)}
                    className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition"
                  >
                    Got It!
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowPWAPrompt(false);
                      // Continue with sharing anyway
                      startSharing();
                    }}
                    className="w-full bg-gray-100 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-200 transition"
                  >
                    Continue Without Installing
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {savedCodes.length > 0 && (
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                      <Users className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="font-semibold">My Travelers</h2>
                      <p className="text-sm text-white/80">{savedCodes.length} saved</p>
                    </div>
                  </div>
                </div>
                
                <button
                  onClick={() => loadSavedTravelers(savedCodes)}
                  className="w-full bg-white text-purple-600 py-3 rounded-lg font-medium hover:bg-white/90 transition"
                >
                  View All Travelers
                </button>
              </div>
            )}

            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Navigation className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900">I'm Traveling</h2>
                  <p className="text-sm text-gray-500">Share my location</p>
                </div>
              </div>
              
              <input
                type="text"
                placeholder="Enter your name"
                value={travelerName}
                onChange={(e) => setTravelerName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg mb-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              {/* Location Privacy Toggle */}
              <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={approximateLocation}
                    onChange={(e) => setApproximateLocation(e.target.checked)}
                    className="mt-1 w-4 h-4"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">🏙️ Share city location only</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      Shows approximate city center (~11km radius) instead of exact location for privacy
                    </p>
                  </div>
                </label>
              </div>
              
              <button
                onClick={startSharing}
                disabled={loading}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Getting location...
                  </>
                ) : (
                  <>
                    <Share2 className="w-5 h-5" />
                    Start Sharing
                  </>
                )}
              </button>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <Plus className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-gray-900">Track New Traveler</h2>
                  <p className="text-sm text-gray-500">Add someone's location</p>
                </div>
              </div>
              
              <input
                type="text"
                placeholder="Enter share code"
                value={viewerCode}
                onChange={(e) => setViewerCode(e.target.value.toUpperCase())}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg mb-3 focus:outline-none focus:ring-2 focus:ring-green-500 uppercase"
                maxLength={6}
              />
              
              <button
                onClick={viewTravelers}
                disabled={loading}
                className="w-full bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Eye className="w-5 h-5" />
                    Add & View
                  </>
                )}
              </button>
            </div>

            <button
              onClick={() => setView('admin-login')}
              className="w-full bg-gray-100 text-gray-600 py-2 rounded-lg text-sm hover:bg-gray-200 transition"
            >
              🔐 Admin Panel
            </button>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}
          </div>

          <div className="mt-8 text-center text-sm text-gray-500">
            <p>🔒 Secure • 🌍 Real-time • ⚡ Lightweight</p>
          </div>

          <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-4 text-sm">
            <p className="font-semibold text-purple-900 mb-2 text-center">💡 Install as App for Easy Access:</p>
            <div className="space-y-2 text-purple-800">
              <div>
                <p className="font-medium">📱 iPhone (Safari):</p>
                <p className="text-xs">Tap Share button → "Add to Home Screen"</p>
              </div>
              <div>
                <p className="font-medium">🤖 Android (Chrome):</p>
                <p className="text-xs">Tap menu (⋮) → "Add to Home Screen"</p>
              </div>
              <p className="text-xs text-purple-600 mt-2">
                Once installed, your saved travelers will load automatically!
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'traveler') {
    const shareUrl = `${window.location.origin}?code=${shareCode}`;
    
    const handleShare = async () => {
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Track My Location',
            text: `Follow my location in real-time! I'm ${travelerName}.`,
            url: shareUrl
          });
        } catch (err) {
          console.log('Share cancelled or failed:', err);
        }
      } else {
        // Fallback: copy to clipboard
        navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard!');
      }
    };

    const copyCode = () => {
      navigator.clipboard.writeText(shareCode);
      alert('Code copied!');
    };

    const copyLink = () => {
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied!');
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="max-w-md mx-auto pt-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-4">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-3">
                <Navigation className="w-8 h-8 text-green-600 animate-pulse" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Sharing Location</h2>
              <p className="text-gray-600">Hello, {travelerName}!</p>
            </div>

            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-4">
              <p className="text-sm text-gray-600 text-center mb-2">Your Share Code</p>
              <div className="flex items-center justify-center gap-2 mb-2">
                <div className="text-3xl font-bold text-blue-600 text-center tracking-wider">
                  {shareCode}
                </div>
                <button
                  onClick={copyCode}
                  className="p-2 hover:bg-blue-100 rounded-lg transition"
                  title="Copy code"
                >
                  <Share2 className="w-5 h-5 text-blue-600" />
                </button>
              </div>
              <p className="text-xs text-gray-500 text-center">
                Tap the code to copy
              </p>
              
              {/* Viewer Count */}
              <div className="mt-3 pt-3 border-t border-blue-200">
                <div className="flex items-center justify-center gap-2">
                  <Users className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-semibold text-blue-900">
                    {viewerCount} {viewerCount === 1 ? 'person' : 'people'} viewing
                  </span>
                </div>
                <p className="text-xs text-gray-500 text-center mt-1">
                  Updates every 10 seconds
                </p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <button
                onClick={handleShare}
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-4 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition shadow-lg flex items-center justify-center gap-2"
              >
                <Share2 className="w-5 h-5" />
                Share My Location Link
              </button>

              <button
                onClick={copyLink}
                className="w-full bg-blue-100 text-blue-700 py-3 rounded-lg font-medium hover:bg-blue-200 transition flex items-center justify-center gap-2"
              >
                📋 Copy Direct Link
              </button>

              <button
                onClick={() => setShowNotificationSettings(!showNotificationSettings)}
                className="w-full bg-purple-100 text-purple-700 py-3 rounded-lg font-medium hover:bg-purple-200 transition flex items-center justify-center gap-2"
              >
                🔔 Notification Settings
              </button>
            </div>

            {/* Notification Settings Panel */}
            {showNotificationSettings && (
              <div className="mb-6 p-4 bg-purple-50 border-2 border-purple-200 rounded-xl">
                <h3 className="font-semibold text-purple-900 mb-3">⏰ Update Reminders</h3>
                
                {notificationPermission === 'granted' ? (
                  <>
                    <p className="text-sm text-purple-800 mb-4">
                      You'll receive reminders to update your location:
                    </p>
                    
                    {/* 3-way Toggle */}
                    <div className="bg-white rounded-lg p-3 mb-4">
                      <div className="grid grid-cols-3 gap-2">
                        <button
                          onClick={() => handleScheduleChange('hourly')}
                          className={`py-3 px-2 rounded-lg text-sm font-medium transition ${
                            tempNotificationSchedule === 'hourly'
                              ? 'bg-purple-600 text-white shadow-md'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <div className="text-lg mb-1">⏱️</div>
                          <div>Hourly</div>
                        </button>
                        
                        <button
                          onClick={() => handleScheduleChange('every3hours')}
                          className={`py-3 px-2 rounded-lg text-sm font-medium transition ${
                            tempNotificationSchedule === 'every3hours'
                              ? 'bg-purple-600 text-white shadow-md'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <div className="text-lg mb-1">⏰</div>
                          <div>Every 3h</div>
                          <div className="text-xs opacity-80">(recommended)</div>
                        </button>
                        
                        <button
                          onClick={() => handleScheduleChange('daily')}
                          className={`py-3 px-2 rounded-lg text-sm font-medium transition ${
                            tempNotificationSchedule === 'daily'
                              ? 'bg-purple-600 text-white shadow-md'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <div className="text-lg mb-1">📅</div>
                          <div>Daily</div>
                        </button>
                      </div>
                    </div>

                    {/* Save Button */}
                    {scheduleChanged && (
                      <button
                        onClick={saveNotificationSchedule}
                        className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition mb-3"
                      >
                        💾 Save Schedule
                      </button>
                    )}

                    {/* Saved Indicator */}
                    {scheduleSaved && (
                      <div className="bg-green-50 border border-green-200 text-green-800 px-3 py-2 rounded-lg text-sm flex items-center justify-center gap-2 mb-3">
                        <span>✓</span>
                        <span>Saved!</span>
                      </div>
                    )}

                    {/* Test Button */}
                    <button
                      onClick={testNotification}
                      className="w-full bg-purple-100 text-purple-700 py-2 rounded-lg text-sm hover:bg-purple-200 transition"
                    >
                      🧪 Test Notification
                    </button>

                    <div className="mt-3 text-xs text-purple-700 bg-purple-100 p-3 rounded-lg">
                      <p className="font-medium mb-1">How to test:</p>
                      <ol className="list-decimal list-inside space-y-1">
                        <li>Tap "Test Notification" above</li>
                        <li>Close this window</li>
                        <li>Wait 10 seconds</li>
                        <li>Tap the notification to reopen</li>
                      </ol>
                    </div>
                  </>
                ) : notificationPermission === 'denied' ? (
                  <div className="text-sm text-red-700 bg-red-50 p-3 rounded-lg">
                    <p className="font-medium mb-1">❌ Notifications Blocked</p>
                    <p>Please enable notifications in your browser settings to receive reminders.</p>
                  </div>
                ) : (
                  <div className="text-sm text-purple-800">
                    <p className="mb-3">Enable notifications to receive automatic reminders to update your location.</p>
                    <button
                      onClick={() => setupPushNotifications(shareCode)}
                      className="w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 transition"
                    >
                      Enable Notifications
                    </button>
                  </div>
                )}
              </div>
            )}

            {location && (
              <div className="space-y-3 mb-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      {approximateLocation ? 'Approximate Location' : 'Current Location'}
                    </span>
                    <span className="text-xs text-green-600 flex items-center gap-1">
                      <div className="w-2 h-2 bg-green-600 rounded-full animate-pulse" />
                      Live
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-gray-500">Latitude</p>
                      <p className="font-mono text-gray-900">{formatCoord(location.latitude)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Longitude</p>
                      <p className="font-mono text-gray-900">{formatCoord(location.longitude)}</p>
                    </div>
                  </div>
                  {approximateLocation && (
                    <div className="mt-2 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
                      ℹ️ Showing city-level location (~11km radius)
                    </div>
                  )}
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-gray-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">Local Time</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {getLocalTime(location.latitude, location.longitude)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between text-xs text-gray-500 px-1">
                  <span>Accuracy: ±{Math.round(location.accuracy)}m</span>
                  <span>Updated: {getLastUpdateText(location.timestamp)}</span>
                </div>
              </div>
            )}

            <button
              onClick={stopSharing}
              className="w-full bg-red-600 text-white py-3 rounded-lg font-medium hover:bg-red-700 transition"
            >
              Stop Sharing Location
            </button>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
            <p className="font-medium mb-1">🔒 Privacy Note</p>
            <p>Your location is only visible to people with your share code. Stop sharing anytime.</p>
          </div>

          <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-4 text-sm">
            <p className="font-semibold text-purple-900 mb-2">💡 How to Install as App:</p>
            <div className="space-y-2 text-purple-800">
              <div>
                <p className="font-medium">📱 iPhone (Safari):</p>
                <p className="text-xs">Tap Share → "Add to Home Screen"</p>
              </div>
              <div>
                <p className="font-medium">🤖 Android (Chrome):</p>
                <p className="text-xs">Tap menu (⋮) → "Add to Home Screen"</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'saved-travelers') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-100 p-4">
        <div className="max-w-md mx-auto pt-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">My Travelers</h2>
              <p className="text-sm text-gray-600">Tracking {activeTravelers.length} location{activeTravelers.length !== 1 ? 's' : ''}</p>
            </div>
            <button
              onClick={() => setView('home')}
              className="px-4 py-2 bg-white rounded-lg text-gray-700 hover:bg-gray-50 transition shadow-sm"
            >
              + Add More
            </button>
          </div>

          <div className="space-y-4">
            {activeTravelers.map((traveler) => (
              <div key={traveler.code} className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {traveler.name[0].toUpperCase()}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 text-lg">{traveler.name}</h3>
                      <p className="text-xs text-gray-500">Code: {traveler.code}</p>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => removeCodeFromSaved(traveler.code)}
                    className="p-2 hover:bg-red-50 rounded-lg transition text-gray-400 hover:text-red-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {traveler.location && (
                  <div className="space-y-3">
                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-blue-600" />
                          <span className="font-medium text-gray-900 text-sm">
                            {traveler.location.isApproximate ? 'Approximate Location' : 'Location'}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {getLastUpdateText(traveler.location.timestamp)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                          <p className="text-gray-600">Latitude</p>
                          <p className="font-mono text-gray-900">{formatCoord(traveler.location.latitude)}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Longitude</p>
                          <p className="font-mono text-gray-900">{formatCoord(traveler.location.longitude)}</p>
                        </div>
                      </div>
                      {traveler.location.isApproximate && (
                        <div className="mt-2 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
                          ℹ️ City-level location (~11km radius)
                        </div>
                      )}
                    </div>

                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Clock className="w-5 h-5 text-purple-600" />
                          <div>
                            <p className="text-xs text-gray-600">Local Time</p>
                            <p className="text-lg font-bold text-gray-900">
                              {getLocalTime(traveler.location.latitude, traveler.location.longitude)}
                            </p>
                          </div>
                        </div>
                        <div className="text-xs text-green-600 flex items-center gap-1">
                          <div className="w-2 h-2 bg-green-600 rounded-full animate-pulse" />
                          Live
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-100 rounded-lg overflow-hidden mb-3">
                      <iframe
                        width="100%"
                        height="200"
                        frameBorder="0"
                        style={{ border: 0 }}
                        src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${traveler.location.latitude},${traveler.location.longitude}&zoom=15`}
                        allowFullScreen
                        title={`Map of ${traveler.name}'s location`}
                      ></iframe>
                    </div>

                    <a
                      href={`https://www.google.com/maps?q=${traveler.location.latitude},${traveler.location.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full bg-blue-600 text-white text-center py-3 rounded-lg font-medium hover:bg-blue-700 transition text-sm"
                    >
                      📍 Open in Google Maps
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-4 bg-purple-50 border border-purple-200 rounded-lg p-4 text-sm text-purple-800">
            <p className="font-medium mb-1">🔄 Auto-updating every 5 seconds</p>
            <p className="text-xs">Your saved travelers will load automatically when you open the app</p>
          </div>

          <div className="mt-4 bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-sm">
            <p className="font-semibold text-indigo-900 mb-2 text-center">💡 Install as App:</p>
            <div className="space-y-2 text-indigo-800">
              <div>
                <p className="font-medium">📱 iPhone:</p>
                <p className="text-xs">Safari → Share → "Add to Home Screen"</p>
              </div>
              <div>
                <p className="font-medium">🤖 Android:</p>
                <p className="text-xs">Chrome → Menu (⋮) → "Add to Home Screen"</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Admin Login View
  if (view === 'admin-login') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <div className="max-w-md mx-auto pt-12">
          <button
            onClick={() => setView('home')}
            className="mb-4 text-gray-600 hover:text-gray-900 flex items-center gap-2"
          >
            ← Back
          </button>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-900 rounded-full mb-4">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Admin Panel</h1>
              <p className="text-gray-600">View all active travelers</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Password
                </label>
                <input
                  type="password"
                  placeholder="Enter password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && loginAdmin()}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <button
                onClick={loginAdmin}
                disabled={loading}
                className="w-full bg-gray-900 text-white py-3 rounded-lg font-medium hover:bg-gray-800 transition disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Logging in...
                  </>
                ) : (
                  'Login'
                )}
              </button>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}
            </div>

            <div className="mt-6 text-xs text-gray-500 text-center">
              Default password: travel2024 (change in backend code)
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Admin Panel View
  if (view === 'admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <div className="max-w-4xl mx-auto pt-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Admin Panel</h2>
              <p className="text-sm text-gray-600">{allTravelers.length} active traveler{allTravelers.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={refreshAdminData}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                🔄 Refresh
              </button>
              <button
                onClick={logoutAdmin}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              >
                Logout
              </button>
            </div>
          </div>

          {allTravelers.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <p className="text-gray-500 text-lg">No active travelers</p>
            </div>
          ) : (
            <div className="space-y-4">
              {allTravelers.map((traveler) => (
                <div key={traveler.code} className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                        {traveler.name[0].toUpperCase()}
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 text-xl">{traveler.name}</h3>
                        <p className="text-sm text-gray-500">Code: <span className="font-mono font-semibold">{traveler.code}</span></p>
                        <p className="text-xs text-gray-400">
                          Started: {new Date(traveler.startTime).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() => deleteAdminTraveler(traveler.code)}
                      className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
                      title="Delete traveler"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {traveler.location && (
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="bg-blue-50 rounded-lg p-4">
                        <p className="text-xs text-gray-600 mb-1">Location</p>
                        <p className="font-mono text-sm text-gray-900">
                          {formatCoord(traveler.location.latitude)}, {formatCoord(traveler.location.longitude)}
                        </p>
                      </div>

                      <div className="bg-purple-50 rounded-lg p-4">
                        <p className="text-xs text-gray-600 mb-1">Local Time</p>
                        <p className="text-lg font-bold text-gray-900">
                          {getLocalTime(traveler.location.latitude, traveler.location.longitude)}
                        </p>
                      </div>

                      <div className="bg-green-50 rounded-lg p-4">
                        <p className="text-xs text-gray-600 mb-1">Last Update</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {getLastUpdateText(traveler.location.timestamp)}
                        </p>
                        <p className="text-xs text-gray-500">
                          Accuracy: ±{Math.round(traveler.location.accuracy)}m
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex gap-2">
                    <a
                      href={`${window.location.origin}?code=${traveler.code}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 bg-blue-600 text-white text-center py-2 rounded-lg text-sm hover:bg-blue-700 transition"
                    >
                      📱 View Page
                    </a>
                    <a
                      href={`https://www.google.com/maps?q=${traveler.location?.latitude},${traveler.location?.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 bg-green-600 text-white text-center py-2 rounded-lg text-sm hover:bg-green-700 transition"
                    >
                      🗺️ Maps
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
};

export default TravelLocationApp;